// Plain-text itinerary for "Copy as text": what to paste when someone asks when we land or
// what the confirmation code is. Times are local to each place, as printed on the booking.

import type { Bags, Person, Segment, Trip } from './types.ts';
import { daysBetween, formatClock, formatRange, formatShortDate } from './time.ts';
import { flightAirline, operatingAirline } from './airlines.ts';
import { memberForTraveler } from './people.ts';
import { sortByStart } from './stops.ts';
import { airportInfo } from './airports.ts';

type Members = Array<Pick<Person, 'name' | 'bookingNames'>>;

/** "Dec 23 – 30" or "Dec 30 – Jan 2" (no year). */
export function shortRange(start: string, end?: string): string {
  if (!end || end <= start) return formatShortDate(start);
  const [a, b] = [formatShortDate(start), formatShortDate(end)];
  return a.slice(0, 3) === b.slice(0, 3) ? `${a} – ${b.slice(4)}` : `${a} – ${b}`;
}

/** "Carry-on included · 1 checked bag included · next bag $100", or '' when nothing is known. */
export function bagsText(bags: Bags | undefined): string {
  if (!bags) return '';
  const parts: string[] = [];
  if (bags.carryOn === 'included') parts.push('Carry-on included');
  else if (bags.carryOn === 'fee') parts.push('Carry-on costs extra');
  else if (bags.carryOn === 'none') parts.push('No carry-on');
  if (bags.checked !== undefined && bags.checked > 0) {
    parts.push(`${bags.checked} checked bag${bags.checked === 1 ? '' : 's'} included`);
    if (bags.extraBagFee) parts.push(`next bag ${bags.extraBagFee}`);
  } else if (bags.extraBagFee) parts.push(`1st checked bag ${bags.extraBagFee}`);
  else if (bags.checked === 0) parts.push('No checked bag included');
  return parts.join(' · ');
}

/** First names for household members, names as printed for everyone else. */
export function travelerNames(travelers: string[], members: Members): string[] {
  return [...new Set(travelers.filter(Boolean).map((t) => memberForTraveler(members, t)?.name ?? t))];
}

function joinNames(names: string[]): string {
  return names.length <= 2 ? names.join(' & ') : `${names.slice(0, -1).join(', ')} & ${names.at(-1)}`;
}

/** "Los Angeles": the city, else the airport code or station as printed. */
export function cityOf(place: Segment['from']): string {
  return place?.city ?? airportInfo(place?.code)?.city ?? place?.code ?? place?.name ?? '?';
}

/** "New York → Ho Chi Minh City" for one flight or a whole journey (its legs in order). */
export function cityRoute(legs: Pick<Segment, 'from' | 'to'>[]): string {
  return `${cityOf(legs[0]?.from)} → ${cityOf(legs.at(-1)?.to)}`;
}

/** "via Chicago and Tokyo": where a journey changes planes ('' for a direct flight). */
export function viaText(legs: Pick<Segment, 'from' | 'to'>[]): string {
  const stops = [...new Set(legs.slice(0, -1).map((l) => cityOf(l.to)))];
  if (!stops.length) return '';
  return `via ${stops.length === 1 ? stops[0] : `${stops.slice(0, -1).join(', ')} and ${stops.at(-1)}`}`;
}

/** "American AA 8459, operated by Japan Airlines" */
export function flightLabel(s: Pick<Segment, 'provider' | 'number' | 'details'>): string {
  const airline = flightAirline(s);
  const op = operatingAirline(s);
  const name = [airline, s.number].filter(Boolean).join(' ') || 'Flight';
  return op ? `${name}, operated by ${op}` : name;
}

function at(place: Segment['from'], time: string | undefined): string {
  return [place?.code ?? place?.name ?? '?', formatClock(time)].filter(Boolean).join(' ');
}

/** "+1" when a flight or train lands a day (or more) after it leaves. */
function nextDay(s: Segment): string {
  const d = s.end?.date ? daysBetween(s.start.date, s.end.date) : 0;
  return d > 0 ? ` +${d}` : '';
}

/** One line per booking: "Dec 22 · American AA 4451 · LGA 6:50 AM → ORD 8:58 AM · Conf TRVQPX" */
export function segmentLine(s: Segment, members: Members = [], showTravelers = true): string {
  const conf = s.confirmation ? `Conf ${s.confirmation}` : '';
  const who = showTravelers ? joinNames(travelerNames(s.travelers, members)) : '';
  const route = `${at(s.from, s.start.time)} → ${at(s.to, s.end?.time)}${nextDay(s)}`;
  const where = [...new Set([s.place?.name, s.place?.address ?? s.place?.city].filter(Boolean))].join(', ');
  let parts: string[];
  switch (s.type) {
    case 'flight':
      parts = [formatShortDate(s.start.date), flightLabel(s), route];
      break;
    case 'train':
      parts = [formatShortDate(s.start.date), `Train ${s.number ?? ''}`.trim(), route];
      break;
    case 'stay':
      parts = [shortRange(s.start.date, s.end?.date), `Stay: ${where || s.title}`];
      break;
    case 'car':
      parts = [shortRange(s.start.date, s.end?.date), `Car: ${[s.provider, s.place?.name].filter(Boolean).join(', ') || s.title}`];
      break;
    case 'activity':
      parts = [formatShortDate(s.start.date), formatClock(s.start.time), s.title || where];
      break;
    default:
      parts = [formatShortDate(s.start.date), formatClock(s.start.time), where || s.title];
  }
  return [...parts, conf, who].filter(Boolean).join(' · ');
}

/**
 * One booking to share with friends (Michael, Oct 22): no confirmation code or names, and a
 * flight goes by its number alone. "Dec 22 · AA 4451 · LGA 6:50 AM → ORD 8:58 AM"
 */
export function shareLine(s: Segment): string {
  if (s.type !== 'flight') return segmentLine({ ...s, confirmation: undefined }, [], false);
  const route = `${at(s.from, s.start.time)} → ${at(s.to, s.end?.time)}${nextDay(s)}`;
  return [formatShortDate(s.start.date), s.number || flightLabel(s), route].join(' · ');
}

/** Picked bookings to share, in date order (cancelled ones left out). */
export function shareText(segments: Segment[]): string {
  return sortByStart(segments.filter((s) => s.status !== 'cancelled'))
    .map(shareLine)
    .join('\n');
}

/** A journey to share: "New York → Ho Chi Minh City · Dec 22 – 23", then each flight. */
export function journeyShareText(legs: Segment[]): string {
  const sorted = sortByStart(legs);
  const first = sorted[0];
  const last = sorted.at(-1);
  if (!first || !last) return '';
  const header = [cityRoute(sorted), shortRange(first.start.date, last.end?.date ?? last.start.date)].join(' · ');
  return [header, shareText(sorted)].join('\n');
}

/**
 * The whole trip: a header line, then one line per booking (cancelled ones left out).
 * Travelers go on the header when everyone is on everything, else on each line.
 */
export function tripText(trip: Trip, segments: Segment[], members: Members = []): string {
  const active = sortByStart(segments.filter((s) => s.tripId === trip.id && s.status !== 'cancelled'));
  const sets = active.map((s) => joinNames(travelerNames(s.travelers, members)));
  const shared = sets.length > 0 && sets.every((x) => x === sets[0]) ? sets[0] : '';
  const header = [trip.title, formatRange(trip.startDate, trip.endDate), shared].filter(Boolean).join(' · ');
  return [header, ...active.map((s) => segmentLine(s, members, !shared))].join('\n');
}
